import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import Sidebar from "../components/Sidebar";
import api from "../lib/api";
import { Check, Sparkles, Loader2 } from "lucide-react";

const FEATURES = [
  "Tous les candidats visibles (illimité)",
  "Matching IA complet avec scores",
  "Réservation illimitée",
  "Publication illimitée de besoins",
  "Recommandations triées par pertinence",
];

export default function Pricing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentTier, setCurrentTier] = useState("free");
  const [currentLabel, setCurrentLabel] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      const [{ data: p }, { data: me }] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/me"),
      ]);
      setPlans(p.plans);
      setCurrentTier(me.tier);
      setCurrentLabel(me.label);
    })();
  }, []);

  const checkout = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
      setBusy(null);
    }
  };

  const downgrade = async () => {
    if (!window.confirm("Repasser en plan gratuit ?")) return;
    await api.post("/billing/downgrade");
    setCurrentTier("free");
  };

  return (
    <div className="flex bg-[#F7F8F6] min-h-screen">
      {user?.role === "pharmacy" && <Sidebar />}
      <main className="flex-1 min-w-0">
        <div className="max-w-6xl mx-auto p-6 lg:p-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>
              Formules d&apos;abonnement
            </h1>
            <p className="text-[#6B726A] mt-3 max-w-xl mx-auto">
              Un seul abonnement, tout inclus. Plus l&apos;engagement est long, plus le tarif mensuel baisse.
            </p>
            {currentTier !== "free" && (
              <div className="mt-4 inline-flex items-center gap-2 text-sm bg-[#E8F0EA] text-[#1A4D2E] px-4 py-2 rounded-full" data-testid="current-plan-banner">
                <Sparkles className="w-3.5 h-3.5" />
                Abonnement actuel : <strong>{currentLabel || currentTier}</strong>
                <button onClick={downgrade} className="ml-2 underline-offset-2 hover:underline text-[#E3655B]" data-testid="downgrade-btn">
                  Passer en gratuit
                </button>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((p) => {
              const isCurrent = p.id === currentTier;
              const isFeatured = p.id === "m6";
              return (
                <div key={p.id} data-testid={`plan-${p.id}`}
                  className={`rp-card p-7 relative flex flex-col ${isFeatured ? "ring-2 ring-[#1A4D2E] lg:-mt-4" : ""}`}>
                  {isFeatured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1A4D2E] text-white text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap">
                      Plus populaire
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-medium" style={{ fontFamily: "Outfit" }} data-testid={`plan-label-${p.id}`}>
                      Engagement {p.label}
                    </h3>
                    {p.discount_pct > 0 && (
                      <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-[#FDEBD2] text-[#B4690E]" data-testid={`plan-discount-${p.id}`}>
                        -{p.discount_pct}%
                      </span>
                    )}
                  </div>
                  <div className="mb-1">
                    <span className="text-4xl font-medium" style={{ fontFamily: "Outfit" }} data-testid={`plan-monthly-${p.id}`}>{p.monthly_eur}€</span>
                    <span className="text-sm text-[#6B726A]"> HT / mois</span>
                  </div>
                  <div className="text-xs text-[#6B726A] mb-6" data-testid={`plan-total-${p.id}`}>
                    Paiement total : <strong className="text-[#0F2A1B]">{Math.round(p.price_eur)} € HT</strong>
                    {p.months > 1 ? ` pour ${p.months} mois` : ""}
                  </div>
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {FEATURES.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#1A4D2E] mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => isCurrent ? null : checkout(p.id)}
                    disabled={isCurrent || busy === p.id}
                    data-testid={`checkout-${p.id}`}
                    className={`rounded-full py-3 text-sm font-medium transition-all ${
                      isCurrent
                        ? "bg-[#F0F2EF] text-[#6B726A] cursor-not-allowed"
                        : isFeatured
                          ? "bg-[#1A4D2E] text-white hover:bg-[#143C24]"
                          : "border border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F0EA]"
                    } flex items-center justify-center gap-2`}
                  >
                    {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {isCurrent ? "Abonnement actuel" : `Choisir ${p.label}`}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-[#6B726A] mt-8">
            Tarifs HT. Paiement sécurisé via Stripe.
          </p>
        </div>
      </main>
    </div>
  );
}
